import { addLineItemsToCurrentCart } from '@wix/ambassador-ecom-v2-cart/http';
import { searchOrders } from '@wix/ambassador-ecom-v1-order/http';
import type { Image } from '@wix/ambassador-ecom-v1-order/types';
import { queryProducts } from '@wix/ambassador-stores-catalog-v1-product/http';
import { WIX_NEW_STORES, WIX_STORES } from '@wix/app-definition-ids';
import type { IHttpClient } from '@wix/yoshi-flow-editor';

const WIX_IMAGE_PREFIX = 'wix:image://v1/';

export interface OrderItem {
  catalogItemId: string;
  productName: string;
  formattedPrice: string;
  imageUrl?: string;
  productPagePath?: string;
  productPageHref?: string;
  catalogOptions?: Record<string, any>;
  inStock?: boolean;
}

const buildStaticMediaUrl = (mediaId: string): string =>
  `https://static.wixstatic.com/media/${mediaId}`;

const toImageUrl = (image: Image | undefined): string | undefined => {
  if (!image) {
    return undefined;
  }
  if (image.url) {
    return image.url;
  }
  if (image.id) {
    if (image.id.startsWith(WIX_IMAGE_PREFIX)) {
      const mediaId = image.id.slice(WIX_IMAGE_PREFIX.length).split('/')[0];
      return buildStaticMediaUrl(mediaId);
    }
    return buildStaticMediaUrl(image.id);
  }
  return undefined;
};

export async function fetchOrderItems(
  httpClient: IHttpClient,
): Promise<OrderItem[]> {
  const { data } = await httpClient.request(
    searchOrders({
      search: {
        filter: { paymentStatus: 'PAID' },
        cursorPaging: { limit: 1 },
      },
    }),
  );

  const items: OrderItem[] = [];
  const seen = new Set<string>();

  for (const lineItem of data.orders?.[0]?.lineItems ?? []) {
    const catalogItemId = lineItem.catalogReference?.catalogItemId;
    const appId = lineItem.catalogReference?.appId;
    if (!catalogItemId || (appId !== WIX_STORES && appId !== WIX_NEW_STORES) || seen.has(catalogItemId)) {
      continue;
    }
    seen.add(catalogItemId);
    const options = lineItem.catalogReference?.options as
      | Record<string, any>
      | undefined;
    items.push({
      catalogItemId,
      productName:
        lineItem.productName?.translated ??
        lineItem.productName?.original ??
        '',
      formattedPrice: lineItem.price?.formattedAmount ?? '',
      imageUrl: toImageUrl(lineItem.image),
      ...(options && Object.keys(options).length > 0 && { catalogOptions: options }),
    });
  }

  return items;
}

const isProductInStock = (product: {
  stock?: { inventoryStatus?: string } | null;
}): boolean => product.stock?.inventoryStatus !== 'OUT_OF_STOCK';

const toProductPagePath = (pageUrl?: {
  base?: string | null;
  path?: string | null;
}): string | undefined => pageUrl?.path ?? undefined;

const toAbsoluteProductPageUrl = (pageUrl?: {
  base?: string | null;
  path?: string | null;
}): string | undefined => {
  if (!pageUrl?.path) {
    return undefined;
  }
  const base = (pageUrl.base ?? '').replace(/\/$/, '');
  const path = pageUrl.path.startsWith('/')
    ? pageUrl.path
    : `/${pageUrl.path}`;
  return `${base}${path}`;
};

export async function enrichWithStockStatus(
  httpClient: IHttpClient,
  items: OrderItem[],
): Promise<OrderItem[]> {
  if (items.length === 0) {
    return items;
  }
  try {
    const ids = items.map((item) => item.catalogItemId);
    const { data } = await httpClient.request(
      queryProducts({
        query: {
          filter: JSON.stringify({ id: { $hasSome: ids } }),
          paging: { limit: ids.length },
        },
      }),
    );
    const productMap = new Map<
      string,
      { inStock: boolean; productPagePath?: string; productPageHref?: string }
    >();
    for (const product of data.products ?? []) {
      if (product.id) {
        productMap.set(product.id, {
          inStock: isProductInStock(product),
          productPagePath: toProductPagePath(product.productPageUrl),
          productPageHref: toAbsoluteProductPageUrl(product.productPageUrl),
        });
      }
    }
    return items.map((item) => {
      const match = productMap.get(item.catalogItemId);
      return {
        ...item,
        inStock: match?.inStock ?? false,
        ...(match?.productPagePath && { productPagePath: match.productPagePath }),
        ...(match?.productPageHref && { productPageHref: match.productPageHref }),
      };
    });
  } catch {
    return items.map((item) => ({ ...item, inStock: true }));
  }
}

export async function fetchInStockOrderItems(
  httpClient: IHttpClient,
  max: number,
): Promise<OrderItem[]> {
  const items = await fetchOrderItems(httpClient);
  const enriched = await enrichWithStockStatus(httpClient, items);
  return enriched.filter((item) => item.inStock !== false).slice(0, max);
}

// Best-effort: scans a single catalog page for in-stock products.
const STORE_PRODUCTS_PAGE_SIZE = 20;

export async function fetchInStockStoreProductsAsOrderItems(
  httpClient: IHttpClient,
  limit: number,
): Promise<OrderItem[]> {
  const { data } = await httpClient.request(
    queryProducts({
      query: {
        filter: JSON.stringify({ visible: true }),
        paging: { limit: STORE_PRODUCTS_PAGE_SIZE },
      },
    }),
  );

  const items: OrderItem[] = [];
  for (const product of data.products ?? []) {
    if (!product.id || !isProductInStock(product)) {
      continue;
    }
    items.push({
      catalogItemId: product.id,
      productName: product.name ?? '',
      formattedPrice: product.priceData?.formatted?.price ?? '',
      imageUrl: product.media?.mainMedia?.image?.url ?? undefined,
      productPagePath: toProductPagePath(product.productPageUrl),
    });
    if (items.length >= limit) {
      break;
    }
  }

  return items;
}

// Throws if the catalog fetch fails — the caller decides how to degrade.
export async function buildEditorBuyAgainItems(
  httpClient: IHttpClient,
  placeholders: OrderItem[],
  max: number,
): Promise<OrderItem[]> {
  const realItems = await fetchInStockStoreProductsAsOrderItems(httpClient, max);
  return [...realItems, ...placeholders].slice(0, max);
}

export async function addToCart(
  httpClient: IHttpClient,
  catalogItemId: string,
  catalogOptions?: Record<string, any>,
): Promise<void> {
  await httpClient.request(
    addLineItemsToCurrentCart({
      catalogItems: [
        {
          catalogReference: {
            catalogItemId,
            appId: WIX_NEW_STORES,
            ...(catalogOptions && { options: catalogOptions }),
          },
          quantity: 1,
        },
      ],
    }),
  );
}
